import { FC, useCallback, useEffect, useState } from 'react';
import { ChatWindow } from '../../pages/chat/ChatWindow';
import { ChatAside } from '../../pages/chat/chat-list/ChatAside';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useChat } from '../../libs/hooks/useChat_v2';
import { setViewType, showAlertToast } from '../../redux/features/app/appSlice';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { AlertType } from '../../libs/models/AlertType';
import { checkMobileKate } from '../utils/common';
import { ChatHeaderOnMobile } from '../../pages/chat/ChatHeaderOnMobile';
import { Constants } from '../../Constants';
// import { PersonalSettingTutorial } from '../../pages/tutorial/PersonalSettingTutorial';
import { useUser } from '../../libs/hooks/useUser';
import { useApp } from '../../libs/hooks/useApp';
import { SurveySwiper } from '../../pages/chat/chat-list/dialogs/detail/SurveySlider';
import { KmsConnectPopup, KMS_GUIDE_KEY } from '../KmsConnectPopup';

export const ChatView: FC = () => {
  //const [isDesktop, setIsDesktop] = useState(!checkMobileKate() || window.innerWidth <= 768);
  const  isDesktop  = useAppSelector((state:RootState) => state.app.viewType == 'Desktop');
  const { selectedId } = useAppSelector((state: RootState) => state.conversations);
  const { chatRoomId } = useParams();

  // const [showTutorial, setShowTutorial] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const dispatch = useDispatch();
  const chat = useChat();
  const user = useUser();
  const apps = useApp();

  useEffect(() => {
    if (chatRoomId && selectedId != chatRoomId) {
      chat.changeChatRoom(chatRoomId).catch(() => {
        dispatch(
          showAlertToast({
            altMessage: String('채팅방에 권한이 없어요. 다른 채팅방을 선택해주세요.'),
            altType: AlertType.Error,
          }),
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRoomId]);

  // useEffect(() => {
  //   if(process.env.REACT_APP_ONBOARDING === 'TRUE' && isDesktop){
  //     user.checkUserPrompt().then((result: boolean)=>{
  //       setShowTutorial(!result);
  //     }).catch(()=>{
  //       setShowTutorial(false);
  //     });
  //   }else{
  //     setShowTutorial(false);
  //   }
    
  // }, [isDesktop]);

  useEffect(() => {
    if(process.env.REACT_APP_SURVEY === 'TRUE' && isDesktop){
      const index = process.env.REACT_APP_SURVEY_INDEX ?? "1";
      user.getSurvey(index).then((result: boolean)=>{
        setShowSurvey(!result);
      }).catch(()=>{
        setShowSurvey(false);
      });
    }else{
      setShowSurvey(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    void (async () => {
      try {
        const company = await user.getCompanyInfo();
        if (company && !company.usePopup) {
          setShowPopup(false);
          return;
        }
        const shouldShow = await user.getGuideShouldShow(KMS_GUIDE_KEY);
        setShowPopup(shouldShow);
      } catch {
        setShowPopup(false);
      }
    })();
  }, []);

  // KMS(Confluence) OAuth 팝업 연결 성공 시 커넥터 목록 갱신
  useEffect(() => {
    const handleAuthResult = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; status?: string } | null;
      if (typeof data === 'object' && data !== null && data.type === 'AUTH_RESULT' && data.status === 'Success') {
        apps.getUserConnectors().catch((e) => {
          console.error('KMS 연결 후 커넥터 갱신 실패:', e);
        });
      }
    };
    window.addEventListener('message', handleAuthResult);
    return () => {
      window.removeEventListener('message', handleAuthResult);
    };
  }, []);

  // collapsed 규칙 :
  // 마우스로 누른게 더 우선이다.
  // 마우스로 누르지 않은 경우에만 자동 여닫기 지원한다.

  const [isAsideCollapsed, setIsAsideCollapsed] = useState(window.innerWidth + 428 <= 1200); // 428 : 포털 widget-area 영역
  const [isAsideClicked, setIsAsideClicked] = useState(false);

  const toggleAside = () => {
    setIsAsideClicked(true);
    setIsAsideCollapsed((prev) => !prev);
  };

  const handleResize = useCallback(() => {
    if (!isAsideClicked) {
      setIsAsideCollapsed(window.innerWidth + 428 <= 1200);
    }
    //setIsDesktop(window.innerWidth > 768);
    dispatch(setViewType(checkMobileKate() || window.innerWidth <= 768 ? 'Mobile' : 'Desktop'));
  }, [isAsideClicked]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // 디바운스를 사용한 커스텀 훅 호출
  useDebouncedEffect(handleResize, Constants.KEYSTROKE_DEBOUNCE_TIME_MS, [isAsideClicked]);

  return (
    <div className={`chat-view`}>
      {/* {showTutorial && <PersonalSettingTutorial setShowTutorial={setShowTutorial}/>} */}
      {showSurvey && (
        <SurveySwiper
          isOpen={true}
          onClose={() => {
            setShowSurvey(false);
          }}
        />
      )}
      {showPopup && (
        <KmsConnectPopup
          isOpen={true}
          onClose={() => {
            setShowPopup(false);
          }}
        />
      )}
      {!isDesktop && <ChatHeaderOnMobile toggleCollapse={toggleAside} />}
      <ChatAside isAsideCollapsed={isAsideCollapsed} toggleAside={toggleAside} />
      <ChatWindow />
    </div>
  );
};

const useDebouncedEffect = (effect: () => void, delay: number, deps: boolean[]) => {
  useEffect(() => {
    const handler = setTimeout(() => {
      effect();
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [effect, delay, deps]);
};
